// store/service.ts
import {
  AuthApi,
  Configuration,
  UserApi,
  OrganizationApi,
  EntryApi,
  FileApi,
  FolderApi,
  ProjectApi
} from '../service';
import { useAtom } from 'jotai';
import { userAtom } from './atoms';
import {
  RevisdomAPIViewModelsUserCreateUserRequest,
  RevisdomAPIViewModelsEntryCreateCommentRequest,
  type RevisdomAPIViewModelsUserResponse,
} from '../service/models';
import {
  ApiFolderPostRequest,
  ApiFolderEditPostRequest,
} from '../service/apis/FolderApi';
import {
  ApiFilePostRequest,
  ApiFileEditPostRequest,
} from '../service/apis/FileApi';
import {
  ApiProjectAllGetScopeEnum,
  ApiProjectEditPostRequest,
} from '../service/apis/ProjectApi';
import { useNavigate } from 'react-router-dom';
import { getPushSubscriptionPayload, type WebPushSubscriptionPayload } from '../push/subscribeWebPush';
import {
  canSearchUserEmails,
  canManageUserAccounts,
  canChangeEntryStatus,
  canMoveOrDuplicateEntry,
  hasRole,
  ROLES_CONTENT_EDITOR,
  ROLES_ACTIVITY_LOGS,
} from '../constants/roles';
import { apiUrl, getApiBasePath } from '../config/apiBasePath';


// Aynı path kısa sürede birden fazla çağrılırsa (AuthChecker + ProjectDetail + FileDetail) tek istek at
const entryByPathCache = new Map<string, { result: any; at: number }>();
const entryByPathInFlight = new Map<string, Promise<any>>();
const ENTRY_PATH_CACHE_MS = 800;
const breadcrumbCache = new Map<string, { result: any; at: number }>();
const breadcrumbInFlight = new Map<string, Promise<any>>();
const BREADCRUMB_CACHE_MS = 800;
const fileByIdCache = new Map<number, { result: any; at: number }>();
const fileByIdInFlight = new Map<number, Promise<any>>();
const FILE_BY_ID_CACHE_MS = 5000;

const apiConfiguration = new Configuration({ basePath: getApiBasePath() });
const baseAuthApi = new AuthApi(apiConfiguration);
const baseUserApi = new UserApi(apiConfiguration);
const baseOrgApi = new OrganizationApi(apiConfiguration);
const baseProjectApi = new ProjectApi(apiConfiguration);
const baseEntryApi = new EntryApi(apiConfiguration);
const baseFileApi = new FileApi(apiConfiguration);
const baseFolderApi = new FolderApi(apiConfiguration);


const postConfig = (additionalHeaders?: Record<string, string>) => {
  const headers = {
    'Content-Type': 'application/json',
    ...additionalHeaders
  };

  return {
    headers,
    credentials: 'include' as RequestCredentials,
    mode: 'cors' as RequestMode
  };
};



export const useSecureService = () => {
  const [user, setUser] = useAtom(userAtom);
  const navigate = useNavigate();

  const normalizeEntryPath = (rawPath: string) => {
    const cleaned = (rawPath || '').trim().replace(/^\/+/, '');
    // URL bazen zaten encoded (%2F) geliyor; önce decode edip canonical hale getiriyoruz.
    try {
      return decodeURIComponent(cleaned);
    } catch {
      return cleaned;
    }
  };

  const handleAuthError = (error: any) => {
    // Sadece 401 Unauthorized = oturum geçersiz, logout yap. 403 Forbidden = yetki yok, logout yapma.
    const status = error?.status ?? error?.response?.status;
    if (status === 401) {
      setUser(undefined);
      const currentPath = window.location.pathname;
      const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
      const isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login', 'results'].includes(pathSegments[0]);
      if (!isFileDetailPage) {
        window.alert('Your session has expired. You are being redirected to the login page.');
        navigate('/login');
      }
    }
  };

  function checkAndHandleAuthError(response: any) {
    if (response && response.success === false && response.status === 401) {
      const currentPath = window.location.pathname;
      const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
      const isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login', 'results'].includes(pathSegments[0]);
      if (!isFileDetailPage) {
        handleAuthError({ status: response.status });
      }
    }
  }

  const doLogin = async (email: string, password: string) => {
    try {
      const result = await baseAuthApi.apiAuthSigninPost({
        revisdomAPIViewModelsSignInRequest: {
          email,
          password
        }
      }, postConfig());

      checkAndHandleAuthError(result);

      if (result.success) {
        let meResult = await baseUserApi.apiUserMeGet(postConfig());
        if (!meResult.success) {
          await new Promise((resolve) => setTimeout(resolve, 150));
          meResult = await baseUserApi.apiUserMeGet(postConfig());
        }
        if (meResult.success) {
          setUser(meResult.data);
        }
        return result;
      }

      return result;
    } catch (err: any) {
      handleAuthError(err);
      return {
        success: false,
        message: 'An error occurred during login'
      };
    }
  };

  const checkAuth = async () => {
    try {
      const result = await baseUserApi.apiUserMeGet(postConfig());
      if (result.success && result.data) {
        setUser(result.data);
        return true;
      }
      setUser(undefined);
      
      // File detail sayfasında ise login yönlendirmesi yapma
      const currentPath = window.location.pathname;
      const fullPath = currentPath.substring(1); // Başındaki /'yi kaldır
      
      let isFileDetailPage = false;
      
      if (fullPath) {
        try {
          // getEntryByPath ile entry tipini kontrol et
          const entryResponse = await getEntryByPath(fullPath);
          if (entryResponse.success && entryResponse.data) {
            isFileDetailPage = entryResponse.data.entryType === 'File' || String(entryResponse.data.entryType) === '2';
            
          }
        } catch (entryError) {
          
          // getEntryByPath başarısız olursa varsayılan kontrolü kullan
          const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
          isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login'].includes(pathSegments[0]);
        }
      }
      
      
      
      if (!isFileDetailPage) {
        
        navigate('/login');
      } else {
        
      }
      
      return false;
    } catch (error: any) {
      setUser(undefined);
      
      // File detail sayfasında ise login yönlendirmesi yapma
      const currentPath = window.location.pathname;
      const fullPath = currentPath.substring(1); // Başındaki /'yi kaldır
      
      let isFileDetailPage = false;
      
      if (fullPath) {
        try {
          // getEntryByPath ile entry tipini kontrol et
          const entryResponse = await getEntryByPath(fullPath);
          if (entryResponse.success && entryResponse.data) {
            isFileDetailPage = entryResponse.data.entryType === 'File' || String(entryResponse.data.entryType) === '2';
            
          }
        } catch (entryError) {
          
          // getEntryByPath başarısız olursa varsayılan kontrolü kullan
          const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
          isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login'].includes(pathSegments[0]);
        }
      }
      
      
      
      if (!isFileDetailPage) {
        
        navigate('/login');
      } else {
        
      }
      
      return false;
    }
  };

  // Düzenli oturum kontrolü için fonksiyon
  const startSessionCheck = () => {
    // Sessiz hata sayacı (ileride eşik vb. için kullanılabilir)
    let _silentFailureCount = 0; // eslint-disable-line @typescript-eslint/no-unused-vars

    const shouldRedirectFor = (obj: any) => {
      try {
        const msg = (obj?.message || '').toString().toLowerCase();
        const status = obj?.status || obj?.statusCode || obj?.response?.status;
        return status === 401 || status === 419 || msg.includes('unauthorized') || msg.includes('session expired');
      } catch {
        return false;
      }
    };

    const checkOnce = async () => {
      try {
        const result = await baseUserApi.apiUserMeGet(postConfig());
        if (result?.success && result.data) {
          setUser(result.data);
          _silentFailureCount = 0;
          return;
        }

        if (shouldRedirectFor(result)) {
          setUser(undefined);

          // File detail sayfasında isek yönlendirme yapma
          const currentPath = window.location.pathname;
          const fullPath = currentPath.substring(1);
          let isFileDetailPage = false;

          if (fullPath) {
            try {
              const entryResponse = await getEntryByPath(fullPath);
              if (entryResponse.success && entryResponse.data) {
                isFileDetailPage = entryResponse.data.entryType === 'File' || String(entryResponse.data.entryType) === '2';
              }
            } catch (entryError) {
              const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
              isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login'].includes(pathSegments[0]);
            }
          }

          if (!isFileDetailPage) {
            window.alert('Your session has expired. You are being redirected to the login page.');
            navigate('/login');
          }
        } else {
          // Ağ hataları vb. durumlarda sessiz kal, kullanıcıyı düşürme
          _silentFailureCount++;
        }
      } catch (error: any) {
        if (shouldRedirectFor(error)) {
          setUser(undefined);

          const currentPath = window.location.pathname;
          const fullPath = currentPath.substring(1);
          let isFileDetailPage = false;

          if (fullPath) {
            try {
              const entryResponse = await getEntryByPath(fullPath);
              if (entryResponse.success && entryResponse.data) {
                isFileDetailPage = entryResponse.data.entryType === 'File' || String(entryResponse.data.entryType) === '2';
              }
            } catch (entryError) {
              const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
              isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login'].includes(pathSegments[0]);
            }
          }

          if (!isFileDetailPage) {
            window.alert('Your session has expired. You are being redirected to the login page.');
            navigate('/login');
          }
        } else {
          // Ağ hatası vb. durumlar – sessiz kal
          _silentFailureCount++;
        }
      }
    };

    // 10 dakikada bir kontrol et
    const checkInterval = setInterval(checkOnce, 10 * 60 * 1000); // 10 dakika

    // Cleanup fonksiyonu döndür
    return () => {
      clearInterval(checkInterval);
    };
  };

  const doLogout = async () => {
    try {
      await baseAuthApi.apiAuthLogoutPost(postConfig());
      setUser(undefined);
      return true;
    } catch (error: any) {
      handleAuthError(error);
      return false;
    }
  };

  const getOrganizations = async () => {
    if (!canManageUserAccounts(user?.role)) {
      return { success: false, message: 'You do not have permission to list organizations.' };
    }
    try {
      const response = await baseOrgApi.apiOrganizationListGet(postConfig());
      checkAndHandleAuthError(response);
      return response;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading organizations'
      };
    }
  };

  const createUser = async (userData: RevisdomAPIViewModelsUserCreateUserRequest) => {
    if (!canManageUserAccounts(user?.role)) {
      return { success: false, message: 'You do not have permission to create users.' };
    }
    try {
      const response = await baseUserApi.apiUserPost({
        revisdomAPIViewModelsUserCreateUserRequest: userData
      }, postConfig());
      checkAndHandleAuthError(response);
      return response;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while creating the user'
      };
    }
  };

  const createProject = async (data: {
    title: string;
    path: string;
    scope?: ApiProjectAllGetScopeEnum;
    location?: string;
    organization?: string;
    share?: string[];
    status?: number;
    thumbnail?: File | null;
  }) => {
    if (!canManageUserAccounts(user?.role)) {
      return { success: false, message: 'You do not have permission to create a project.' };
    }
    try {
      const formData = new FormData();
      formData.append('Name', data.title); // API Name field'ı bekliyor
      formData.append('Title', data.title);
      formData.append('Path', data.path);
      if (data.scope) formData.append('Scope', data.scope);
      if (data.location) formData.append('Location', data.location);
      if (data.organization != null && data.organization !== '') formData.append('Organization', data.organization);
      if (data.status !== undefined) formData.append('Status', String(data.status));
      if (data.share && data.share.length > 0) {
        data.share.forEach(email => formData.append('Share', email));
      }
      if (data.thumbnail) formData.append('Thumbnail', data.thumbnail);

      const response = await fetch(apiUrl('/api/Project'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('createProject error:', response.status, errorText);
        if (response.status === 401) {
          navigate('/login');
          return { success: false, message: 'Session expired', response: errorText };
        }
        return { success: false, message: `HTTP ${response.status}: ${errorText}`, response: errorText };
      }
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while creating the project'
      };
    }
  };

  const getProjects = async (scope?: ApiProjectAllGetScopeEnum) => {
    try {
      const response = await baseProjectApi.apiProjectAllGet({ scope }, postConfig());
      checkAndHandleAuthError(response);
      return response;
    } catch (error: any) {
      console.error('getProjects error:', error);
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading projects'
      };
    }
  };

  const getEnrtyDetail = async (params: { projectId?: number; folderId?: number }) => {
    try {
      const response = await baseEntryApi.apiEntryGet(params, postConfig());
      checkAndHandleAuthError(response);
      return response;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading entry detail'
      };
    }
  };

  const getEntryByPath = async (path: string) => {
    const normalizedPath = normalizeEntryPath(path);
    const now = Date.now();
    const cached = entryByPathCache.get(normalizedPath);
    if (cached && now - cached.at < ENTRY_PATH_CACHE_MS) {
      return typeof cached.result === 'object' && cached.result !== null
        ? { ...cached.result }
        : cached.result;
    }

    const inFlight = entryByPathInFlight.get(normalizedPath);
    if (inFlight) return inFlight;

    const request = (async () => {
      try {
        const result = await baseEntryApi.apiEntryUrlGet({ url: normalizedPath }, postConfig());
        checkAndHandleAuthError(result);
        entryByPathCache.set(normalizedPath, { result, at: Date.now() });
        return result;
      } catch (error: any) {
        const status = error?.status ?? error?.response?.status;
        handleAuthError({ ...error, status });
        if (status != null) {
          if (status === 403) {
            try {
              entryByPathCache.delete(normalizedPath);
            } catch {
              // ignore
            }
          }
          return {
            success: false,
            message: status === 403 ? 'Forbidden' : (error?.message ?? `HTTP ${status}`),
            status,
            accessDenied: status === 403,
          };
        }
        return {
          success: false,
          message: 'An error occurred while loading entry detail',
        };
      } finally {
        entryByPathInFlight.delete(normalizedPath);
      }
    })();

    entryByPathInFlight.set(normalizedPath, request);
    return request;
  };

  const getBreadcrumb = async (url: string) => {
    const normalizedPath = normalizeEntryPath(url);
    const now = Date.now();
    const cached = breadcrumbCache.get(normalizedPath);
    if (cached && now - cached.at < BREADCRUMB_CACHE_MS) {
      return typeof cached.result === 'object' && cached.result !== null
        ? { ...cached.result }
        : cached.result;
    }

    const inFlight = breadcrumbInFlight.get(normalizedPath);
    if (inFlight) return inFlight;

    const request = (async () => {
      try {
        const result = await baseEntryApi.apiEntryBreadcrumbUrlGet(
          { url: normalizedPath },
          postConfig()
        );
        checkAndHandleAuthError(result);
        breadcrumbCache.set(normalizedPath, { result, at: Date.now() });
        return result;
      } catch (error: any) {
        handleAuthError(error);
        if (error?.status != null) {
          return {
            success: false,
            message: error?.message ?? `HTTP ${error.status}`,
          };
        }
        return {
          success: false,
          message: 'An error occurred while loading breadcrumb',
        };
      } finally {
        breadcrumbInFlight.delete(normalizedPath);
      }
    })();

    breadcrumbInFlight.set(normalizedPath, request);
    return request;
  };

  const getDownloadLinks = async (url: string) => {
    try {
      const normalizedPath = normalizeEntryPath(url);
      if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        console.log('[getDownloadLinks] GET /api/Entry/{url}/download-links, url:', normalizedPath);
      }
      const result = await baseEntryApi.apiEntryUrlDownloadLinksGet({ url: normalizedPath }, postConfig());
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      if (error?.status != null) {
        return {
          success: false,
          message: error?.message ?? `HTTP ${error.status}`
        };
      }
      return {
        success: false,
        message: 'An error occurred while loading download links'
      };
    }
  };

  const getEntryByProjectId = async (projectId: number) => {
    try {
      const response = await baseEntryApi.apiEntryGet({ projectId }, postConfig());
      checkAndHandleAuthError(response);
      return response;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading project entries'
      };
    }
  };

  const getEntryById = async (entryId: number) => {
    try {
      const response = await fetch(apiUrl(`/api/Entry/${entryId}`), {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Service: HTTP Error:', response.status, errorText);
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      
      const result = await response.json();
     checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading entry details'
      };
    }
  };

  const getFileById = async (fileId: number) => {
    const now = Date.now();
    const cached = fileByIdCache.get(fileId);
    if (cached && now - cached.at < FILE_BY_ID_CACHE_MS) {
      return typeof cached.result === 'object' && cached.result !== null
        ? { ...cached.result }
        : cached.result;
    }

    const inFlight = fileByIdInFlight.get(fileId);
    if (inFlight) return inFlight;

    const request = (async () => {
      try {
        const response = await fetch(apiUrl(`/api/File/${fileId}`), {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          const errorText = await response.text();
          return {
            success: false,
            message: `HTTP ${response.status}: ${errorText}`,
            status: response.status,
            accessDenied: response.status === 403,
          };
        }

        const result = await response.json();
        checkAndHandleAuthError(result);
        fileByIdCache.set(fileId, { result, at: Date.now() });
        return result;
      } catch (error: any) {
        handleAuthError(error);
        return {
          success: false,
          message: 'An error occurred while loading file details',
        };
      } finally {
        fileByIdInFlight.delete(fileId);
      }
    })();

    fileByIdInFlight.set(fileId, request);
    return request;
  };

  const getFileActivityLogs = async (fileId: number) => {
    if (!user || !hasRole(user.role, ...ROLES_ACTIVITY_LOGS)) {
      return { success: false, message: 'You do not have permission to view activity logs.' };
    }
    try {
      const response = await fetch(
        apiUrl(`/api/File/${fileId}/activity-logs`),
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`,
        };
      }

      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading activity logs',
      };
    }
  };

  const createFolder = async (data: ApiFolderPostRequest) => {
    if (!hasRole(user?.role, ...ROLES_CONTENT_EDITOR)) {
      return { success: false, message: 'You do not have permission to create a folder.' };
    }
    try {

      const formData = new FormData();
      if (data.name) formData.append('Name', data.name);
      if (data.parentId !== undefined) formData.append('ParentId', String(data.parentId));
      if (data.projectId !== undefined) formData.append('ProjectId', String(data.projectId));
      if (data.share && data.share.length > 0) {
        data.share.forEach(email => formData.append('Share', email));
      }
      if (data.thumbnail) formData.append('Thumbnail', data.thumbnail);
      
      // FormData içeriğini logla
      formData.forEach((value, key) => {
        
      });

      const response = await fetch(apiUrl('/api/Folder'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      if (!response.ok && response.status === 401) {
        setUser(undefined);
        const currentPath = window.location.pathname;
        const isFileDetailPage = currentPath.split('/').length >= 2;
        if (!isFileDetailPage) {
          navigate('/login');
        }
        return { success: false, message: 'Session expired' };
      }
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while creating the folder'
      };
    }
  };

  const createFile = async (data: ApiFilePostRequest) => {
    if (!hasRole(user?.role, ...ROLES_CONTENT_EDITOR)) {
      return { success: false, message: 'You do not have permission to create a file.' };
    }
    try {
      const formData = new FormData();
      if (data.name) formData.append('Name', data.name);
      if (data.folderId !== undefined) formData.append('FolderId', String(data.folderId));
      if (data.projectId !== undefined) formData.append('ProjectId', String(data.projectId));
      if (data.status !== undefined) formData.append('Status', data.status);
      if (data.share && data.share.length > 0) {
        data.share.forEach(email => formData.append('Share', email));
      }
      if (data.thumbnail) formData.append('Thumbnail', data.thumbnail);
      if (data.file) formData.append('File', data.file);
      if (data.fileId !== undefined) formData.append('FileId', String(data.fileId));

      const response = await fetch(apiUrl('/api/File'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 401) {
          setUser(undefined);
          const currentPath = window.location.pathname;
          const isFileDetailPage = currentPath.split('/').length >= 2;
          if (!isFileDetailPage) {
            navigate('/login');
          }
          return { success: false, message: 'Session expired' };
        }
        return { success: false, message: `HTTP ${response.status}: ${errorText}` };
      }
      
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while creating the file'
      };
    }
  };

  const uploadWithPath = async (params: {
    projectId: number;
    folderId?: number | null;
    relativePath: string;
    file: File;
    thumbnail?: Blob;
    onProgress?: (percent: number) => void;
  }): Promise<{ success: boolean; message?: string; data?: any }> => {
    if (!hasRole(user?.role, ...ROLES_CONTENT_EDITOR)) {
      return { success: false, message: 'You do not have permission to upload here.' };
    }
    try {
      const formData = new FormData();
      formData.append('ProjectId', String(params.projectId));
      if (params.folderId !== undefined && params.folderId !== null && params.folderId !== 0) {
        formData.append('FolderId', String(params.folderId));
      }
      formData.append('RelativePath', params.relativePath);
      formData.append('File', params.file);
      if (params.thumbnail) {
        formData.append('Thumbnail', params.thumbnail);
      }

      const useXhr = typeof params.onProgress === 'function';
      const url = apiUrl('/api/Entry/upload-with-path');

      if (useXhr) {
        return new Promise((resolve) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url);
          xhr.withCredentials = true;
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && e.total > 0) {
              const p = Math.min(85, Math.round((e.loaded / e.total) * 85));
              params.onProgress?.(p);
            }
          };
          xhr.onload = () => {
            if (xhr.status === 401) {
              setUser(undefined);
              const currentPath = window.location.pathname;
              const isFileDetailPage = currentPath.split('/').length >= 2;
              if (!isFileDetailPage) navigate('/login');
              resolve({ success: false, message: 'Session expired' });
              return;
            }
            if (xhr.status < 200 || xhr.status >= 300) {
              resolve({ success: false, message: xhr.responseText || `HTTP ${xhr.status}` });
              return;
            }
            try {
              const result = JSON.parse(xhr.responseText || '{}');
              checkAndHandleAuthError(result);
              const success = result?.Success ?? result?.success;
              resolve(typeof success === 'boolean' ? { ...result, success } : result);
            } catch {
              resolve({ success: false, message: 'Invalid response' });
            }
          };
          xhr.onerror = () => resolve({ success: false, message: 'Network error' });
          xhr.send(formData);
        });
      }

      const response = await fetch(url, { method: 'POST', body: formData, credentials: 'include' });
      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 401) {
          setUser(undefined);
          const currentPath = window.location.pathname;
          const isFileDetailPage = currentPath.split('/').length >= 2;
          if (!isFileDetailPage) navigate('/login');
          return { success: false, message: 'Session expired' };
        }
        return { success: false, message: errorText || `HTTP ${response.status}` };
      }
      const result = await response.json();
      checkAndHandleAuthError(result);
      const success = result?.Success ?? result?.success;
      return typeof success === 'boolean' ? { ...result, success } : result;
    } catch (error: any) {
      handleAuthError(error);
      return { success: false, message: error?.message || 'An error occurred while uploading the file' };
    }
  };

  const deleteEntry = async (entries: Array<{ id: number; entryType: string }>) => {
    if (!hasRole(user?.role, ...ROLES_CONTENT_EDITOR)) {
      return { success: false, message: 'You do not have permission to delete this item.' };
    }
    try {
      const response = await fetch(apiUrl('/api/Entry'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ entries }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('deleteEntry HTTP Error:', response.status, errorText);
        
        if (response.status === 401) {
          const currentPath = window.location.pathname;
          const pathSegments = currentPath.split('/').filter(p => p && p.trim() !== '');
          const isFileDetailPage = pathSegments.length >= 2 && !['dashboard', 'internal-projects', 'projects', 'external-projects', 'settings', 'help', 'login', 'results'].includes(pathSegments[0]);
          if (!isFileDetailPage) {
            window.alert('Your session has expired. You are being redirected to the login page.');
            navigate('/login');
          }
          return { success: false, message: 'Unauthorized' };
        }
        
        return { 
          success: false, 
          message: `HTTP ${response.status}: ${errorText}` 
        };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('deleteEntry error:', error);
      return { success: false, message: 'Failed to delete entry' };
    }
  };

  const changeStatus = async (entries: Array<{
    id: number;
    entryType: string;
    entryStatus: string;
    changeSubentries: boolean
  }>) => {
    if (!canChangeEntryStatus(user?.role)) {
      return { success: false, message: 'You do not have permission to change status.' };
    }
    try {
      const response = await fetch(apiUrl('/api/Entry/change-status'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ entries }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('changeStatus HTTP Error:', response.status, errorText);

        if (response.status === 401) {
          return { success: false, message: 'Unauthorized' };
        }

        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('changeStatus error:', error);
      return { success: false, message: 'Failed to change status' };
    }
  };

  const duplicateEntry = async (params: { id: number; entryType: string }) => {
    if (!canMoveOrDuplicateEntry(user?.role)) {
      return { success: false, message: 'You do not have permission to duplicate this item.' };
    }
    try {
      const result = await baseEntryApi.apiEntryDuplicatePost(
        {
          revisdomAPIViewModelsEntryDuplicateEntryRequest: {
            id: params.id,
            entryType: params.entryType as 'Project' | 'Folder' | 'File' | 'Version',
          },
        },
        postConfig()
      );
      checkAndHandleAuthError(result);
      const success = result?.success ?? false;
      return { success, data: result?.data, message: result?.message };
    } catch (error: any) {
      handleAuthError(error);
      return { success: false, message: error?.message ?? 'An error occurred while duplicating the entry' };
    }
  };

  const moveEntry = async (params: {
    sourceId: number;
    sourceEntryType: 'Folder' | 'File';
    targetProjectId: number;
    targetParentId: number | null;
  }) => {
    if (!canMoveOrDuplicateEntry(user?.role)) {
      return { success: false, message: 'You do not have permission to move this item.' };
    }
    try {
      const result = await baseEntryApi.apiEntryMovePost(
        {
          revisdomAPIViewModelsEntryMoveEntryRequest: {
            sourceId: params.sourceId,
            sourceEntryType: params.sourceEntryType,
            targetProjectId: params.targetProjectId,
            targetParentId: params.targetParentId,
          },
        },
        postConfig()
      );
      checkAndHandleAuthError(result);
      const success = result?.success ?? false;
      return { success, data: result?.data, message: result?.message };
    } catch (error: any) {
      handleAuthError(error);
      return { success: false, message: error?.message ?? 'An error occurred while moving the entry' };
    }
  };

  const refreshUserInfo = async () => {
    try {
      const result = await baseUserApi.apiUserMeGet(postConfig());
      if (result.success) {
        setUser(result.data);
        return result.data;
      } else {
        return null;
      }
    } catch (error: any) {
      console.error('refreshUserInfo error:', error);
      
      // 401 hatası durumunda user'ı temizle
      if (error?.status === 401 || error?.response?.status === 401) {
        setUser(undefined);
        return null;
      }

      return null;
    }
  };

  const createFileComment = async (fileId: number, data: RevisdomAPIViewModelsEntryCreateCommentRequest) => {
    try {

      // API'ye doğrudan data gönder
      const response = await fetch(apiUrl('/api/File/comment'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Service: HTTP Error:', response.status, errorText);
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      
      const result = await response.json();

      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while creating the file comment'
      };
    }
  };

  const createGuestFileComment = async (fileId: number, data: RevisdomAPIViewModelsEntryCreateCommentRequest & {
    firstName: string;
    lastName: string;
    email: string;
  }) => {
    try {
      const response = await fetch(apiUrl('/api/File/guest-comment'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Service: HTTP Error:', response.status, errorText);
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      
      const result = await response.json();
      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'An error occurred while creating the guest comment'
      };
    }
  };

  // Yorumun resolve bilgisini değiştirir
  const resolveFileComment = async (commentId: number, isResolved: boolean) => {
    try {
      const response = await fetch(apiUrl('/api/File/comment/resolve'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: commentId, isResolved })
      });
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while resolving the comment'
      };
    }
  };

  const pinFileComment = async (commentId: number, isPinned: boolean) => {
    try {
      const response = await fetch(apiUrl('/api/File/comment/pin'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: commentId, isPinned }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`,
        };
      }
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while updating pin',
      };
    }
  };

  // Comment düzenleme
  const editFileComment = async (commentId: number, data: { text: string; data: string }) => {
    try {
      const response = await fetch(apiUrl('/api/File/comment'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: commentId, ...data })
      });
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while editing the comment'
      };
    }
  };

  // Comment silme
  const deleteFileComment = async (commentId: number) => {
    try {
      const response = await fetch(apiUrl('/api/File/comment'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: commentId })
      });
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while deleting the comment'
      };
    }
  };

  const getAllUserEmails = async (searchTerm?: string) => {
    if (!canSearchUserEmails(user?.role)) {
      return { success: false, data: [] as string[] };
    }
    try {
      const params = searchTerm ? { searchTerm } : {};
      const response = await baseUserApi.apiUserEmailsGet(params, postConfig());
      if (response.success && response.data && response.data.emails) {
        return {
          success: true,
          data: response.data.emails
        };
      }
      return { success: false, data: [] };
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading user emails',
        data: []
      };
    }
  };

  const getAllUsers = async () => {
    if (!canManageUserAccounts(user?.role)) {
      return { success: false, data: [], message: 'You do not have permission to list users.' };
    }
    try {
      const response = await baseUserApi.apiUserGet(postConfig());
      checkAndHandleAuthError(response);
      if (response.success) {
        return {
          success: true,
          data: response.data?.users ?? [],
          message: response.message ?? null,
        };
      }
      return {
        success: false,
        data: [] as RevisdomAPIViewModelsUserResponse[],
        message: response.message ?? 'Failed to load users',
      };
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        data: [],
        message: 'An error occurred while loading users',
      };
    }
  };

  const deleteUserById = async (id: number) => {
    if (!canManageUserAccounts(user?.role)) {
      return { success: false, message: 'You do not have permission to delete users.' };
    }
    try {
      const response = await baseUserApi.apiUserIdDelete({ id }, postConfig());
      checkAndHandleAuthError(response);
      return response;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while deleting the user',
      };
    }
  };

  const getUserNotifications = async () => {
    try {
      const response = await fetch(apiUrl('/api/User/notifications'), {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      console.error('Fetch error:', error);
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while loading notifications'
      };
    }
  };

  const changeNotificationStatus = async (notificationIds: number[], isRead: boolean) => {
    try {
      const response = await fetch(apiUrl('/api/User/notifications/change-status'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          notificationIds,
          isRead
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          message: `HTTP ${response.status}: ${errorText}`
        };
      }
      
      const result = await response.json();
      checkAndHandleAuthError(result);
      return result;
    } catch (error: any) {
      handleAuthError(error);
      return {
        success: false,
        message: 'An error occurred while updating notification status'
      };
    }
  };

  // Edit functions for sharing
  const editFile = async (data: ApiFileEditPostRequest) => {
    if (!hasRole(user?.role, ...ROLES_CONTENT_EDITOR)) {
      return { success: false, message: 'You do not have permission to edit this file.' } as any;
    }
    try {
      // Multipart form data için özel config
      const formData = new FormData();
      if (data.id) formData.append('Id', data.id.toString());
      if (data.name) formData.append('Name', data.name);
      if (data.share) {
        data.share.forEach(email => formData.append('Share', email));
      }
      // Thumbnail varsa ekle
      if (data.thumbnail) {
        formData.append('Thumbnail', data.thumbnail);
      }

      const result = await baseFileApi.apiFileEditPost({
        id: data.id,
        name: data.name,
        thumbnail: data.thumbnail,
        share: data.share
      }, { 
        credentials: 'include',
        mode: 'cors'
      });
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  const editFolder = async (data: ApiFolderEditPostRequest) => {
    if (!hasRole(user?.role, ...ROLES_CONTENT_EDITOR)) {
      return { success: false, message: 'You do not have permission to edit this folder.' } as any;
    }
    try {
      const result = await baseFolderApi.apiFolderEditPost({
        id: data.id,
        name: data.name,
        thumbnail: data.thumbnail,
        share: data.share
      }, { 
        credentials: 'include',
        mode: 'cors'
      });
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  const editProject = async (data: ApiProjectEditPostRequest) => {
    if (!canManageUserAccounts(user?.role)) {
      return { success: false, message: 'You do not have permission to edit this project.' } as any;
    }
    try {
      const result = await baseProjectApi.apiProjectEditPost({
        id: data.id,
        name: data.name,
        thumbnail: data.thumbnail,
        share: data.share
      }, { 
        credentials: 'include',
        mode: 'cors'
      });
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  const toggleProjectPin = async (projectId: number) => {
    if (!canManageUserAccounts(user?.role)) {
      return { success: false, message: 'You do not have permission to pin projects.' } as any;
    }
    try {
      const result = await baseProjectApi.apiProjectIdTogglePinPost(
        { id: projectId },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }, credentials: 'include', mode: 'cors' }
      );
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  const editUser = async (data: {
    firstName?: string;
    lastName?: string;
    title?: string;
    oldPassword?: string;
    newPassword?: string;
    thumbnail?: File;
    notificationToEmail?: boolean;
  }) => {
    if (!canManageUserAccounts(user?.role)) {
      return { success: false, message: 'You do not have permission to update this profile.' } as any;
    }
    try {
      const result = await baseUserApi.apiUserEditPost({
        firstName: data.firstName,
        lastName: data.lastName,
        title: data.title,
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
        thumbnail: data.thumbnail,
        notificationToEmail: data.notificationToEmail
      } as any, { 
        credentials: 'include',
        mode: 'cors'
      });
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  // Dashboard için entry count servisi
  const getEntryCount = async (status?: string, startDate?: string) => {
    try {
      const result = await baseEntryApi.apiEntryCountGet({
        startDate: startDate ? new Date(startDate) : undefined
      }, postConfig());
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  // Dashboard için user dashboard servisi
  const getUserDashboard = async (startDate?: string) => {
    if (!canManageUserAccounts(user?.role)) {
      return { success: false, message: 'You do not have permission to load dashboard parameters.' } as any;
    }
    try {
      const result = await baseUserApi.apiUserDashboardGet({
        startDate: startDate ? new Date(startDate) : undefined
      }, postConfig());
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  // Dashboard için entry summary servisi (scope: Internal | External, result için)
  const getEntrySummary = async (status?: string, startDate?: string, scope?: 'Internal' | 'External') => {
    try {
      const result = await baseEntryApi.apiEntrySummaryGet({
        startDate: startDate ? new Date(startDate) : undefined,
        status: status as any,
        scope: scope as any
      }, postConfig());
      checkAndHandleAuthError(result);
      return result;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  };

  // Web Push subscription backend'e gönder. Backend Auth, P256dh, Endpoint alanlarını zorunlu tutuyor — sadece geçerli payload ile çağrı yapılmalı.
  const subscribeWebPushToBackend = async (payload: WebPushSubscriptionPayload) => {
    try {
      const result = await baseUserApi.apiUserSubscribeWebPushPost(
        {
          revisdomAPIViewModelsUserSubscribeWebPushRequest: {
            endpoint: payload.endpoint,
            p256dh: payload.p256dh,
            auth: payload.auth,
          },
        },
        postConfig()
      );
      return result as any;
    } catch (err: any) {
      return { success: false, message: err?.message || 'Failed to subscribe to web push' };
    }
  };

  // Banner'dan "Allow" tıklandığında: izin iste, granted ise payload al ve backend'e gönder
  const subscribeWebPushIfAllowed = async () => {
    const payload = await getPushSubscriptionPayload(); // requestPermission default true
    if (!payload) return { success: false };
    return subscribeWebPushToBackend(payload);
  };

  // Her login'de çağrılır; sadece izin granted ve subscription varsa API'ye gider (backend boş body kabul etmiyor).
  const subscribeWebPushOnLogin = async () => {
    const payload = await getPushSubscriptionPayload({ requestPermission: false });
    if (!payload) return { success: false };
    return subscribeWebPushToBackend(payload);
  };

  return {
    doLogin,
    doLogout,
    getOrganizations,
    createUser,
    createProject,
    getProjects,
    getEnrtyDetail,
    getEntryByPath,
    getBreadcrumb,
    getDownloadLinks,
    getEntryByProjectId,
    getEntryById,
    getFileById,
    getFileActivityLogs,
    createFolder,
    createFile,
    uploadWithPath,
    deleteEntry,
    changeStatus,
    duplicateEntry,
    refreshUserInfo,
    checkAuth,
    startSessionCheck,
    createFileComment,
    createGuestFileComment,
    getAllUserEmails,
    getAllUsers,
    deleteUserById,
    resolveFileComment,
    pinFileComment,
    editFileComment,
    deleteFileComment,
    getUserNotifications,
    changeNotificationStatus,
    editFile,
    editFolder,
    editProject,
    moveEntry,
    toggleProjectPin,
    editUser,
    getEntryCount,
    getUserDashboard,
    getEntrySummary,
    subscribeWebPushIfAllowed,
    subscribeWebPushOnLogin
  };
};